import "./Login.css";
import { Button, Col, Form, Row } from "react-bootstrap";
import { Eye, EyeOff } from "lucide-react";
import { Link } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import AuthBrandPanel from "../components/AuthBrandPanel";
import axios from "axios";

const ACCENT = "#27b99a";
const BRAND = "#064260";
const MUTED = "#475569";
const RESEND_COOLDOWN_SECONDS = 30;
const SLATE = "#0f172a";

const ForgotPassword = () => {
	const [confirmPassword, setConfirmPassword] = useState("");
	const [email, setEmail] = useState("");
	const [errorMessage, setErrorMessage] = useState("");
	const [isLoading, setIsLoading] = useState(false);
	const [isResending, setIsResending] = useState(false);
	const [otp, setOtp] = useState(Array(6).fill(""));
	const [password, setPassword] = useState("");
	const [resendCooldown, setResendCooldown] = useState(0);
	const [showConfirmPassword, setShowConfirmPassword] = useState(false);
	const [showPassword, setShowPassword] = useState(false);
	const [step, setStep] = useState(1);
	const [successMessage, setSuccessMessage] = useState("");

	const otpRefs = useRef([]);
	const resendTimerRef = useRef(null);

	useEffect(
		() => () => {
			if (resendTimerRef.current) {
				clearInterval(resendTimerRef.current);
			}
		},
		[],
	);

	const clearMessages = () => {
		setErrorMessage("");
		setSuccessMessage("");
	};

	const startResendCooldown = () => {
		if (resendTimerRef.current) {
			clearInterval(resendTimerRef.current);
		}

		setResendCooldown(RESEND_COOLDOWN_SECONDS);
		resendTimerRef.current = setInterval(() => {
			setResendCooldown((secondsRemaining) => {
				if (secondsRemaining <= 1) {
					clearInterval(resendTimerRef.current);
					resendTimerRef.current = null;
					return 0;
				}
				return secondsRemaining - 1;
			});
		}, 1000);
	};

	const validateEmail = (value) => {
		const pattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
		return pattern.test(String(value).toLowerCase());
	};

	const validatePassword = (value) => {
		const pattern = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/;
		return pattern.test(value);
	};

	const handleEmailSubmit = async (event) => {
		event.preventDefault();
		if (!validateEmail(email)) {
			setErrorMessage("Invalid email format.");
			setSuccessMessage("");
			return;
		}

		clearMessages();
		setIsLoading(true);

		try {
			const response = await axios.post(`${process.env.REACT_APP_BASE_URL}/admin/forgot-password`, {
				email,
			});

			if (response.data.success) {
				setStep(2);
				setOtp(Array(6).fill(""));
				startResendCooldown();
			} else {
				setErrorMessage("Email is not registered.");
			}
		} catch (error) {
			if (!navigator.onLine) {
				setErrorMessage("Network connection lost. Please check your connection and try again.");
			} else {
				setErrorMessage(error.response?.data?.message || "An error occurred. Please try again.");
			}
		} finally {
			setIsLoading(false);
		}
	};

	const handleOtpChange = (value, index) => {
		const digit = value.replace(/\D/g, "").slice(-1);
		const nextOtp = [...otp];
		nextOtp[index] = digit;
		setOtp(nextOtp);

		if (digit && index < otpRefs.current.length - 1) {
			otpRefs.current[index + 1].focus();
		}
	};

	const handleOtpKeyDown = (event, index) => {
		if (event.key === "Backspace" && !otp[index] && index > 0) {
			otpRefs.current[index - 1].focus();
		}
	};

	const handleOtpPaste = (event) => {
		event.preventDefault();
		const pastedData = event.clipboardData.getData("text").replace(/\D/g, "").slice(0, otp.length);
		if (!pastedData) {
			return;
		}

		const nextOtp = Array(6).fill("");
		pastedData.split("").forEach((char, index) => {
			nextOtp[index] = char;
		});
		setOtp(nextOtp);
		otpRefs.current[Math.min(pastedData.length, otp.length) - 1]?.focus();
	};

	const handleOtpSubmit = async (event) => {
		event.preventDefault();
		if (otp.includes("")) {
			setErrorMessage("Please fill all OTP fields.");
			setSuccessMessage("");
			return;
		}

		clearMessages();
		setIsLoading(true);

		try {
			const response = await axios.post(`${process.env.REACT_APP_BASE_URL}/admin/verify-otp`, {
				email,
				otp: otp.join(""),
			});

			if (response.data.success) {
				setStep(3);
			} else {
				setErrorMessage("OTP is incorrect. Please try again.");
			}
		} catch (error) {
			if (!navigator.onLine) {
				setErrorMessage("Network connection lost. Please check your connection and try again.");
			} else {
				setErrorMessage(error.response?.data?.message || "An error occurred. Please try again.");
			}
		} finally {
			setIsLoading(false);
		}
	};

	const handleResendCode = async () => {
		if (resendCooldown > 0 || isResending) {
			return;
		}

		clearMessages();
		setIsResending(true);

		try {
			const response = await axios.post(`${process.env.REACT_APP_BASE_URL}/admin/forgot-password`, {
				email,
			});

			if (response.data.success) {
				setOtp(Array(6).fill(""));
				setSuccessMessage("A new code has been sent to your email.");
				startResendCooldown();
				otpRefs.current[0]?.focus();
			} else {
				setErrorMessage(response.data.message || "Unable to resend code. Please try again.");
			}
		} catch (error) {
			if (!navigator.onLine) {
				setErrorMessage("Network connection lost. Please check your connection and try again.");
			} else {
				setErrorMessage(error.response?.data?.message || "An error occurred. Please try again.");
			}
		} finally {
			setIsResending(false);
		}
	};

	const handlePasswordSubmit = async (event) => {
		event.preventDefault();
		if (!validatePassword(password)) {
			setErrorMessage("Password does not meet the required criteria.");
			setSuccessMessage("");
			return;
		}
		if (password !== confirmPassword) {
			setErrorMessage("Passwords do not match.");
			setSuccessMessage("");
			return;
		}

		setIsLoading(true);
		clearMessages();

		try {
			const response = await axios.post(`${process.env.REACT_APP_BASE_URL}/admin/reset-password`, {
				email,
				password,
			});

			if (response.data.success) {
				setSuccessMessage("Password reset successfully.");
				setTimeout(() => {
					window.location.href = "/login";
				}, 1000);
			} else {
				setErrorMessage("Failed to reset password. Please try again.");
			}
		} catch (error) {
			if (!navigator.onLine) {
				setErrorMessage("Network connection lost. Please check your connection and try again.");
			} else {
				setErrorMessage(error.response?.data?.message || "An error occurred. Please try again.");
			}
		} finally {
			setIsLoading(false);
		}
	};

	const backToSignIn = (
		<Link style={{ color: ACCENT, fontSize: "14px", fontWeight: 500, textDecoration: "none" }} to="/login">
			Back to Sign In
		</Link>
	);

	return (
		<Row className="auth-page g-0 min-vh-100">
			<AuthBrandPanel />

			<Col
				className="d-flex align-items-center justify-content-center"
				lg={8}
				md={7}
				style={{ backgroundColor: "#f8fafc", padding: "60px" }}
				xs={12}>
				<div className="w-100" style={{ maxWidth: "400px" }}>
					{step === 1 ? (
						<Form className="d-flex flex-column" onSubmit={handleEmailSubmit} style={{ gap: "32px" }}>
							<div className="d-flex flex-column" style={{ gap: "8px" }}>
								<h2 className="mb-0" style={{ color: SLATE, fontSize: "28px", fontWeight: 600 }}>
									Reset your password
								</h2>
								<p className="mb-0" style={{ color: MUTED, fontSize: "14px", lineHeight: 1.4 }}>
									Enter your email and we&apos;ll send you a reset link
								</p>
							</div>

							<Form.Group controlId="forgot-email">
								<Form.Label className="mb-2" style={{ color: SLATE, fontSize: "14px", fontWeight: 600 }}>
									Email
								</Form.Label>
								<Form.Control
									autoComplete="email"
									className="auth-page__input"
									onChange={(event) => setEmail(event.target.value)}
									placeholder="name@organization.com"
									style={{
										backgroundColor: "#ffffff",
										border: "1px solid #cbd5e1",
										borderRadius: "8px",
										fontSize: "14px",
										padding: "12px",
									}}
									type="email"
									value={email}
								/>
							</Form.Group>

							<div className="d-flex flex-column align-items-center" style={{ gap: "20px" }}>
								{errorMessage ? (
									<p
										className="mb-0 text-center w-100"
										role="alert"
										style={{ color: "#dc2626", fontSize: "14px", fontWeight: 500 }}>
										{errorMessage}
									</p>
								) : null}
								<Button
									className="w-100 border-0"
									disabled={isLoading}
									style={{
										backgroundColor: BRAND,
										borderRadius: "8px",
										fontSize: "16px",
										fontWeight: 600,
										padding: "14px",
									}}
									type="submit"
									variant="primary">
									{isLoading ? "Sending..." : "Send Reset Link"}
								</Button>
								{backToSignIn}
							</div>
						</Form>
					) : null}

					{step === 2 ? (
						<Form className="d-flex flex-column" onSubmit={handleOtpSubmit} style={{ gap: "32px" }}>
							<div className="d-flex flex-column" style={{ gap: "8px" }}>
								<h2 className="mb-0" style={{ color: SLATE, fontSize: "28px", fontWeight: 600 }}>
									Enter verification code
								</h2>
								<p className="mb-0" style={{ color: MUTED, fontSize: "14px", lineHeight: 1.4 }}>
									We sent a 6-digit security code to your registered email address.
								</p>
							</div>

							<Form.Group>
								<Form.Label className="mb-2" style={{ color: SLATE, fontSize: "14px", fontWeight: 600 }}>
									Security Code
								</Form.Label>
								<div className="d-flex" style={{ gap: "12px" }}>
									{otp.map((digit, index) => (
										<Form.Control
											aria-label={`Digit ${index + 1}`}
											autoComplete="one-time-code"
											className="auth-page__otp"
											inputMode="numeric"
											key={`otp-${index}`}
											maxLength={1}
											onChange={(event) => handleOtpChange(event.target.value, index)}
											onKeyDown={(event) => handleOtpKeyDown(event, index)}
											onPaste={handleOtpPaste}
											placeholder="-"
											ref={(element) => {
												otpRefs.current[index] = element;
											}}
											type="text"
											value={digit}
										/>
									))}
								</div>
							</Form.Group>

							<div className="d-flex flex-column align-items-center" style={{ gap: "20px" }}>
								{errorMessage ? (
									<p
										className="mb-0 text-center w-100"
										role="alert"
										style={{ color: "#dc2626", fontSize: "14px", fontWeight: 500 }}>
										{errorMessage}
									</p>
								) : null}
								{successMessage ? (
									<p
										className="mb-0 text-center w-100"
										role="status"
										style={{ color: ACCENT, fontSize: "14px", fontWeight: 500 }}>
										{successMessage}
									</p>
								) : null}
								<Button
									className="w-100 border-0"
									disabled={isLoading}
									style={{
										backgroundColor: BRAND,
										borderRadius: "8px",
										fontSize: "16px",
										fontWeight: 600,
										padding: "14px",
									}}
									type="submit"
									variant="primary">
									{isLoading ? "Verifying..." : "Verify Code"}
								</Button>
								<div
									className="d-flex flex-column align-items-center"
									style={{ gap: "12px", fontSize: "14px" }}>
									<p className="mb-0 text-center" style={{ color: MUTED }}>
										Didn&apos;t receive the code?{" "}
										<button
											disabled={isResending || resendCooldown > 0}
											onClick={handleResendCode}
											style={{
												background: "none",
												border: 0,
												color: ACCENT,
												cursor: isResending || resendCooldown > 0 ? "not-allowed" : "pointer",
												fontFamily: "inherit",
												fontSize: "14px",
												fontWeight: 600,
												opacity: isResending || resendCooldown > 0 ? 0.6 : 1,
												padding: 0,
												textDecoration: "underline",
											}}
											type="button">
											{isResending
												? "Sending..."
												: resendCooldown > 0
													? `Resend Code (${resendCooldown}s)`
													: "Resend Code"}
										</button>
									</p>
									{backToSignIn}
								</div>
							</div>
						</Form>
					) : null}

					{step === 3 ? (
						<Form className="d-flex flex-column" onSubmit={handlePasswordSubmit} style={{ gap: "32px" }}>
							<div className="d-flex flex-column" style={{ gap: "8px" }}>
								<h2 className="mb-0" style={{ color: SLATE, fontSize: "28px", fontWeight: 600 }}>
									Create new password
								</h2>
								<p className="mb-0" style={{ color: MUTED, fontSize: "14px", lineHeight: 1.4 }}>
									Your identity has been verified. Please choose a secure password that you haven&apos;t used
									before.
								</p>
							</div>

							<div className="d-flex flex-column" style={{ gap: "16px" }}>
								<Form.Group className="position-relative" controlId="new-password">
									<Form.Label className="mb-2" style={{ color: SLATE, fontSize: "14px", fontWeight: 600 }}>
										New Password
									</Form.Label>
									<Form.Control
										autoComplete="new-password"
										className="auth-page__input"
										onChange={(event) => setPassword(event.target.value)}
										placeholder="••••••••••••"
										style={{
											backgroundColor: "#ffffff",
											border: "1px solid #cbd5e1",
											borderRadius: "8px",
											fontSize: "14px",
											padding: "12px 40px 12px 12px",
										}}
										type={showPassword ? "text" : "password"}
										value={password}
									/>
									<Button
										aria-label={showPassword ? "Hide password" : "Show password"}
										className="position-absolute border-0 bg-transparent p-0"
										onClick={() => setShowPassword(!showPassword)}
										style={{ color: MUTED, right: "12px", top: "42px" }}
										type="button"
										variant="link">
										{showPassword ? (
											<EyeOff color={MUTED} size={16} strokeWidth={2} />
										) : (
											<Eye color={MUTED} size={16} strokeWidth={2} />
										)}
									</Button>
									<p className="mb-0 mt-2" style={{ color: MUTED, fontSize: "12px", lineHeight: 1.4 }}>
										Must be at least 8 characters long with one uppercase letter, one lowercase letter, a
										number, and a symbol.
									</p>
								</Form.Group>

								<Form.Group className="position-relative" controlId="confirm-password">
									<Form.Label className="mb-2" style={{ color: SLATE, fontSize: "14px", fontWeight: 600 }}>
										Confirm Password
									</Form.Label>
									<Form.Control
										autoComplete="new-password"
										className="auth-page__input"
										onChange={(event) => setConfirmPassword(event.target.value)}
										placeholder="••••••••••••"
										style={{
											backgroundColor: "#ffffff",
											border: "1px solid #cbd5e1",
											borderRadius: "8px",
											fontSize: "14px",
											padding: "12px 40px 12px 12px",
										}}
										type={showConfirmPassword ? "text" : "password"}
										value={confirmPassword}
									/>
									<Button
										aria-label={showConfirmPassword ? "Hide password" : "Show password"}
										className="position-absolute border-0 bg-transparent p-0"
										onClick={() => setShowConfirmPassword(!showConfirmPassword)}
										style={{ color: MUTED, right: "12px", top: "42px" }}
										type="button"
										variant="link">
										{showConfirmPassword ? (
											<EyeOff color={MUTED} size={16} strokeWidth={2} />
										) : (
											<Eye color={MUTED} size={16} strokeWidth={2} />
										)}
									</Button>
								</Form.Group>
							</div>

							<div className="d-flex flex-column align-items-center" style={{ gap: "20px" }}>
								{errorMessage ? (
									<p
										className="mb-0 text-center w-100"
										role="alert"
										style={{ color: "#dc2626", fontSize: "14px", fontWeight: 500 }}>
										{errorMessage}
									</p>
								) : null}
								{successMessage ? (
									<p
										className="mb-0 text-center w-100"
										role="status"
										style={{ color: ACCENT, fontSize: "14px", fontWeight: 500 }}>
										{successMessage}
									</p>
								) : null}
								<Button
									className="w-100 border-0"
									disabled={isLoading}
									style={{
										backgroundColor: BRAND,
										borderRadius: "8px",
										fontSize: "16px",
										fontWeight: 600,
										padding: "14px",
									}}
									type="submit"
									variant="primary">
									{isLoading ? "Resetting..." : "Reset Password"}
								</Button>
								{backToSignIn}
							</div>
						</Form>
					) : null}
				</div>
			</Col>
		</Row>
	);
};

export default ForgotPassword;
